import { useSnackbar } from "notistack";
import { createContext, FunctionComponent, useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { useAuthContext } from "../../providers/Auth/auth.provider";
import { useUnsavedDataContext } from "../../providers/App/unsaved-data-context";
import { useDashboardActionContext } from "../../pages/dashboard/dashboard-action.provider";
import { IPomReportingListItem } from "../../domain/reporting/pom-reporting";
import { IPomReportingTableData } from "../../components/reporting-table/pom-reporting-table-data";
import { PomReportingStatus } from "../../repositories/models/reportings/pom-reporting-status";
import { PomQueryKeys } from "../../repositories/pom-query-keys";
import { IPomReportingExcelImportPositionModel } from "../../repositories/models/reportings/pom-reporting-excel-import.model";
import { useGetReportingListQuery } from "../../repositories/queries/reportings/get-reporting-list.query";
import { useGetReportingQuery } from "../../repositories/queries/reportings/get-reporting.query";
import { useGetReportingDraftQuery } from "../../repositories/queries/reportings/get-reporting-draft.query";
import { useGetReportingDraftNosQuery } from "../../repositories/queries/reportings/get-reporting-draft-nos.query";
import { useSubmitReportingMutation } from "../../repositories/queries/mutations/reportings/submit-reporting.query";
import { useSaveReportingDraftMutation } from "../../repositories/queries/mutations/reportings/save-reporting-draft.query";
import { useDeleteReportingDraftMutation } from "../../repositories/queries/mutations/reportings/delete-reporting-draft.query";
import { StatusCodes } from "http-status-codes";
import { hasChangesAgainstBaseline, hasPendingChange, quantitiesEqual } from "./pom-reporting-unsaved-state.util";
import { toTableData } from "./pom-reporting-table-data.util";
import { normalizeQuantity } from "../../components/reporting-table/reporting-quantity.helper";

interface IPomReportingProviderContext {
  currentContractNo: string | undefined;
  currentTakeBackSystemName: string | null | undefined;
  currentReportingNo: number | undefined;
  currentStatus: PomReportingStatus | undefined;
  reportingList: IPomReportingListItem[];
  positions: IPomReportingTableData[];
  isLoading: boolean;
  isPositionsError: boolean;
  isDirty: boolean;
  hasDraft: boolean;
  isSavingDraft: boolean;
  draftReportingNos: number[];
  hasUnsavedDraftChanges: boolean;
  hasUnsavedDataForNavigation: boolean;
  isImportDialogOpen: boolean;
  isExportDialogOpen: boolean;
  expandedPositionNos: Set<number>;

  handleReportingSelected: (reportingNo: number) => void;
  onQuantityChange: (positionNo: number, quantity: number) => void;
  handleToggleHistory: (positionNo: number) => void;
  submitReporting: () => Promise<boolean>;
  resetChanges: () => void;
  saveDraft: () => Promise<void>;
  discardDraft: () => Promise<void>;
  openImportDialog: () => void;
  closeImportDialog: () => void;
  openExportDialog: () => void;
  closeExportDialog: () => void;
  applyImportedPositions: (positions: IPomReportingExcelImportPositionModel[]) => void;
}

const PomReportingContext = createContext<IPomReportingProviderContext>({} as IPomReportingProviderContext);

export const usePomReportingContext = () => {
  return useContext(PomReportingContext);
};

const usePomReportingProvider = (): IPomReportingProviderContext => {
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();
  const { internalUser } = useAuthContext();
  const { contractId } = useDashboardActionContext();
  const { setHasUnsavedData } = useUnsavedDataContext();
  const queryClient = useQueryClient();

  const [currentReportingNo, setCurrentReportingNo] = useState<number | undefined>(undefined);
  const [positions, setPositions] = useState<IPomReportingTableData[]>([]);
  const [lastSavedDraftMap, setLastSavedDraftMap] = useState<Map<number, number> | null>(null);
  const [isImportDialogOpen, setIsImportDialogOpen] = useState<boolean>(false);
  const [isExportDialogOpen, setIsExportDialogOpen] = useState<boolean>(false);
  const [expandedPositionNos, setExpandedPositionNos] = useState<Set<number>>(new Set());

  const currentContract = useMemo(() => {
    return internalUser?.group?.pomContracts?.find((c) => c.contractNumber === contractId);
  }, [contractId, internalUser]);

  const currentContractNo = currentContract?.contractNumber;
  const currentTakeBackSystemName = currentContract?.takeBackSystemName;

  const {
    data: reportingList = [],
    isLoading: isLoadingList,
    refetch: refetchList,
  } = useGetReportingListQuery(currentContractNo);

  const currentStatus = useMemo(() => {
    if (currentReportingNo === undefined) return undefined;
    const item = reportingList.find((r) => r.reportingNo === currentReportingNo);
    return item?.status;
  }, [reportingList, currentReportingNo]);

  const {
    data: reportingPositions,
    isLoading: isLoadingPositions,
    isError: isPositionsQueryError,
    refetch: refetchPositions,
  } = useGetReportingQuery(currentContractNo, currentReportingNo);
  // A failed background refetch keeps the cached rows, so only a load that produced nothing is an error.
  const isPositionsError = isPositionsQueryError && reportingPositions === undefined;

  const { mutateAsync: submitReportingMutation } = useSubmitReportingMutation();

  const { data: draftData } = useGetReportingDraftQuery(currentContractNo, currentReportingNo);
  const { data: draftReportingNos = [] } = useGetReportingDraftNosQuery(currentContractNo);
  const { mutateAsync: saveDraftMutation, isPending: isSavingDraft } = useSaveReportingDraftMutation();
  const { mutateAsync: deleteDraftMutation } = useDeleteReportingDraftMutation();

  const hasDraft = draftData !== null && draftData !== undefined;

  const isDirty = useMemo(() => {
    return positions.some((p) => {
      const quantity = p.editedQuantity ?? p.currentQuantity;
      return !quantitiesEqual(quantity, p.currentQuantity);
    });
  }, [positions]);

  const hasUnsavedDraftChanges = useMemo(() => {
    return hasChangesAgainstBaseline(positions, lastSavedDraftMap ?? new Map<number, number>());
  }, [positions, lastSavedDraftMap]);

  const hasUnsavedDataForNavigation = hasUnsavedDraftChanges;

  const isLoading = isLoadingList || isLoadingPositions;

  const handleReportingSelected = (reportingNo: number): void => {
    setCurrentReportingNo(reportingNo);
  };

  useEffect(() => {
    setHasUnsavedData(hasUnsavedDataForNavigation);
  }, [hasUnsavedDataForNavigation, setHasUnsavedData]);

  useEffect(() => {
    return () => {
      setHasUnsavedData(false);
    };
  }, [setHasUnsavedData]);

  // Auto-select the oldest open reporting period when the list loads
  useEffect(() => {
    if (reportingList.length > 0 && currentReportingNo === undefined) {
      const oldestOpen = reportingList.find((r) => r.status === PomReportingStatus.Open);
      const fallback = reportingList[reportingList.length - 1];
      setCurrentReportingNo((oldestOpen ?? fallback).reportingNo);
    }
  }, [reportingList, currentReportingNo]);

  // Sync positions from query data when it changes, merging draft quantities
  useEffect(() => {
    if (reportingPositions) {
      setPositions(toTableData(reportingPositions, draftData));
      setLastSavedDraftMap(
        draftData ? new Map(draftData.positions.map((p) => [p.positionNo, p.quantity])) : null,
      );
    } else {
      // Stale rows from the previous period would otherwise be submitted under the new reportingNo.
      setPositions([]);
      setLastSavedDraftMap(null);
    }
  }, [reportingPositions, draftData]);

  const onQuantityChange = (positionNo: number, quantity: number): void => {
    setPositions((prev) =>
      prev.map((p) => (p.positionNo === positionNo ? { ...p, editedQuantity: quantity } : p)),
    );
  };

  const handleToggleHistory = (positionNo: number): void => {
    setExpandedPositionNos((prev) => {
      const next = new Set(prev);
      if (next.has(positionNo)) {
        next.delete(positionNo);
      } else {
        next.add(positionNo);
      }
      return next;
    });
  };

  const submitReporting = async (): Promise<boolean> => {
    if (!currentContractNo || currentReportingNo === undefined) return false;

    const changedPositions = positions.filter(hasPendingChange);
    if (changedPositions.length === 0) return false;

    const result = await submitReportingMutation({
      contractNo: currentContractNo,
      reportingNo: currentReportingNo,
      request: {
        positions: changedPositions.map((p) => ({
          positionNo: p.positionNo,
          quantity: p.editedQuantity as number,
        })),
      },
    });

    if (result.status >= StatusCodes.OK && result.status < StatusCodes.MULTIPLE_CHOICES) {
      enqueueSnackbar(t("pom.reportings.submit.success"), { variant: "success" });
      setLastSavedDraftMap(null);
      setExpandedPositionNos(new Set());
      await Promise.all([
        refetchPositions(),
        refetchList(),
        queryClient.invalidateQueries({
          queryKey: [PomQueryKeys.GetReportingDraft, currentContractNo, currentReportingNo],
        }),
        queryClient.invalidateQueries({
          queryKey: [PomQueryKeys.GetReportingHistory, currentContractNo, currentReportingNo],
        }),
      ]);
      return true;
    } else {
      const message = result.errorMessage
        ? t("pom.reportings.submit.error_with_message", { message: result.errorMessage })
        : t("pom.reportings.submit.error");
      enqueueSnackbar(message, { variant: "error" });
      return false;
    }
  };

  const resetChanges = (): void => {
    setPositions((prev) => prev.map((p) => ({ ...p, editedQuantity: undefined })));
  };

  const saveDraft = async (): Promise<void> => {
    if (!currentContractNo || currentReportingNo === undefined) return;
    const changedPositions = positions
      .filter(hasPendingChange)
      .map((p) => ({ positionNo: p.positionNo, quantity: p.editedQuantity as number }));
    if (changedPositions.length === 0) return;

    try {
      await saveDraftMutation({
        contractNo: currentContractNo,
        reportingNo: currentReportingNo,
        request: { positions: changedPositions },
      });
      setLastSavedDraftMap(new Map(changedPositions.map((p) => [p.positionNo, p.quantity])));
      enqueueSnackbar(t("pom.reportings.draft.savedSuccessfully"), { variant: "success" });
    } catch {
      enqueueSnackbar(t("pom.reportings.draft.saveFailed"), { variant: "error" });
    }
  };

  const discardDraft = async (): Promise<void> => {
    if (!currentContractNo || currentReportingNo === undefined) return;
    try {
      await deleteDraftMutation({ contractNo: currentContractNo, reportingNo: currentReportingNo });
      setLastSavedDraftMap(null);
      resetChanges();
      enqueueSnackbar(t("pom.reportings.draft.discarded"), { variant: "info" });
    } catch {
      enqueueSnackbar(t("pom.reportings.draft.discardFailed"), { variant: "error" });
    }
  };

  const openImportDialog = (): void => {
    setIsImportDialogOpen(true);
  };

  const closeImportDialog = (): void => {
    setIsImportDialogOpen(false);
  };

  const openExportDialog = (): void => {
    setIsExportDialogOpen(true);
  };

  const closeExportDialog = (): void => {
    setIsExportDialogOpen(false);
  };

  const applyImportedPositions = (imported: IPomReportingExcelImportPositionModel[]): void => {
    setPositions((prev) =>
      prev.map((p) => {
        const match = imported.find((i) => i.positionNo === p.positionNo);
        return match !== undefined ? { ...p, editedQuantity: normalizeQuantity(match.quantity, p.unit) } : p;
      })
    );
  };

  return {
    currentContractNo,
    currentTakeBackSystemName,
    currentReportingNo,
    currentStatus,
    reportingList,
    positions,
    isLoading,
    isPositionsError,
    isDirty,
    hasDraft,
    isSavingDraft,
    draftReportingNos,
    hasUnsavedDraftChanges,
    hasUnsavedDataForNavigation,
    isImportDialogOpen,
    isExportDialogOpen,
    expandedPositionNos,
    handleReportingSelected,
    onQuantityChange,
    handleToggleHistory,
    submitReporting,
    resetChanges,
    saveDraft,
    discardDraft,
    openImportDialog,
    closeImportDialog,
    openExportDialog,
    closeExportDialog,
    applyImportedPositions,
  };
};

export const PomReportingProvider: FunctionComponent<React.PropsWithChildren> = (props) => {
  const value = usePomReportingProvider();
  return <PomReportingContext.Provider value={value}>{props.children}</PomReportingContext.Provider>;
};
